import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterBar from '@/components/feature/FilterBar';
import MovieGrid from '@/components/feature/MovieGrid';
import SectionHeading from '@/components/feature/SectionHeading';
import { applyFilter, defaultFilter, getPopular, searchMovies, type FilterState } from '@/utils/movieUtils';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const [input, setInput] = useState(query);
  const [filter, setFilter] = useState<FilterState>({ ...defaultFilter });

  useEffect(() => {
    setInput(query);
    setFilter({ ...defaultFilter });
  }, [query]);

  const results = useMemo(() => applyFilter(searchMovies(query), filter), [query, filter]);
  const popular = useMemo(() => getPopular(10), []);

  useDocumentMeta({
    title: query ? `搜索“${query}” - 午夜视频` : '搜索影视 - 午夜视频',
    description: query
      ? `午夜视频搜索“${query}”的结果，共找到 ${results.length} 部相关影视内容。`
      : '在午夜视频搜索电影、电视剧与短视频，按题材、地区、年份快速筛选。',
    keywords: `午夜视频搜索,${query || '影视搜索'},电影,电视剧,短视频`,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setParams(q ? { q } : {});
  };

  return (
    <div className="pt-16 md:pt-20">
      <section className="border-b border-background-200 bg-background-100">
        <div className="px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />
          <h1 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            {query ? (
              <>
                搜索：
                <span className="text-primary-400">“{query}”</span>
              </>
            ) : (
              '搜索影视资源'
            )}
          </h1>
          <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="flex-1 flex items-center gap-2 rounded-lg bg-background-200/70 border border-background-300 px-4 h-12 focus-within:border-primary-400 transition-colors">
              <i className="ri-search-line text-foreground-500 text-lg"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入片名、题材、导演或演员"
                aria-label="搜索影视内容"
                className="flex-1 bg-transparent text-sm md:text-base text-foreground-950 placeholder:text-foreground-500 outline-none"
              />
            </div>
            <button
              type="submit"
              className="h-12 px-8 rounded-lg bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-400 transition-colors"
            >
              搜索
            </button>
          </form>
        </div>
      </section>

      <section className="px-4 md:px-8 py-6 md:py-8">
        {query ? (
          <>
            <FilterBar filter={filter} onChange={setFilter} />
            <p className="mt-6 text-sm text-foreground-600">
              {results.length > 0 ? (
                <>
                  为你找到 <span className="text-primary-400 font-semibold">{results.length}</span> 部相关影视
                </>
              ) : (
                '暂无匹配结果'
              )}
            </p>

            {results.length > 0 ? (
              <div className="mt-4">
                <MovieGrid items={results} />
              </div>
            ) : (
              <div className="mt-6 flex flex-col items-center justify-center text-center py-16 rounded-lg border border-background-200 bg-background-100">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-500/15 text-accent-400">
                  <i className="ri-emotion-sad-line text-3xl"></i>
                </span>
                <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950">没有找到“{query}”相关的内容</h2>
                <p className="mt-2 text-sm text-foreground-600 max-w-md">
                  换个关键词试试，或者放宽下方的筛选条件。也可以看看下面的热门推荐。
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  {['悬疑', '科幻', '爱情', '动作', '剧情'].map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setParams({ q: tag })}
                      className="px-4 py-2 rounded-full bg-background-200/70 text-sm text-foreground-700 hover:bg-primary-500 hover:text-background-950 transition-colors cursor-pointer"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center text-center py-16">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-400">
              <i className="ri-search-line text-3xl"></i>
            </span>
            <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950">开始你的观影探索</h2>
            <p className="mt-2 text-sm text-foreground-600">输入片名、题材或演员，快速找到想看的影视。</p>
          </div>
        )}

        <div className="mt-12">
          <SectionHeading icon="ri-fire-line" title="热门推荐" subtitle="不知道看什么？从这些热门作品开始" />
          <MovieGrid items={popular} />
        </div>
      </section>
    </div>
  );
}